import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import useForm from "../../hooks/useForm";
import Main from "../../layout/main";
import { AddClassService, ClassesService } from "../../services/competitions";
import { selectClass as selectClassAction } from "../../store/actions";
import Header from "../../components/common/secondary header/index";
import { getMixpanelAdminType, Mixpanel } from "../../services/Mixpanel";
import * as configActions from "../../store/config/actions";
import "./style.scss";
import { textIdentifier } from "../../textIdentifier";


const ClassesList = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useDispatch();
  const { selectedCompetition, selectedSchool, isViewingStatus, lang } = useSelector(
    (state) => state.main
  );
  const { userData } = useSelector((state) => state.auth);

  const [error, setError] = useState("");
  const [classes, setClasses] = useState([]);
  const [showAdminOptions, setShowAdminOptions] = useState(false);
  const [showAddClassModal, setShowAddClassModal] = useState(false);
  const [isFetching, setIsFetching] = useState(true);

  const toggleShowAdminOptions = () => {
    setShowAdminOptions(!showAdminOptions);
  };

  const fetchClasses = async () => {
    try {
      const res = await ClassesService(
        selectedCompetition.id,
        selectedSchool.id
      );
      setClasses(res.data);
      Mixpanel.track("Competition_Admin_Classes_viewed", {
        Admin_Type: getMixpanelAdminType(userData?.groups),
        School_id: selectedSchool?.id,
        CompetitionID: selectedCompetition?.id,
        CompetitionName: selectedCompetition?.name,
      });
      Mixpanel.people.set({
        $email: userData?.email,
        $distinct_id: userData?.id,
      });
    } catch (error) {
      dispatch(configActions.showAlert(error?.data?.message));
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    if (!selectedCompetition || !selectedSchool) {
      navigate(`/`);
    } else {
      fetchClasses();
    }
  }, []);

  const selectClass = (schoolClass) => {
    dispatch(selectClassAction(schoolClass));
    if (!isViewingStatus) {
      navigate(`/schools/${id}/classes/${schoolClass.code}/students`);
    } else {
      navigate(`/schools/${id}/classes/${schoolClass.code}/status`);
    }
  };

  // Add Class
  const handleSubmit = async (values) => {
    try {
      const data = {
        competition_id: selectedCompetition.id,
        class_name: values.className,
        extension: values.classCodeExtension,
      };
      if (Object.keys(addClassForm.errors).length < 1) {
        await AddClassService(selectedSchool.id, data);
        addClassForm.setSubmitting();
        handleCloseAddClassForm();
        fetchClasses();
      }
    } catch (error) {
      addClassForm.setSubmitting();
      setError(error.data?.message || "Error occured");
    }
  };

  const addClassForm = useForm({
    initialValues: {
      className: "",
      classCodeExtension: "",
    },
    validate: (values) => {
      const errors = {};
      if (!values.className.trim()) {
        errors.className = textIdentifier['please-enter-class-name'][lang];
      }
      if (!values.classCodeExtension.trim()) {
        errors.classCodeExtension = textIdentifier["please-enter-class-code-extension"][lang];
      }
      if (Boolean(error)) {
        setError("");
      }
      return errors;
    },
    onSubmit: (values) => {
      handleSubmit(values);
    },
  });

  const handleCloseAddClassForm = () => {
    addClassForm.resetForm({
      className: "",
      classCodeExtension: "",
    });
    addClassForm.setSubmitting();
    setShowAddClassModal(false);
  };

  const startTime = new Date(selectedCompetition?.start_time);
  const endTime = new Date(selectedCompetition?.end_time);

  const renderTimeStringJSX = (start_time, end_time) => {
    const startTime = new Date(start_time);
    const endTime = new Date(end_time);

    function getDateString(date) {
      const d = new Date(date).toJSON();
      return d.split("T")[0].split("-").reverse().join("-");
    }

    return (
      <Fragment>
        <span>{getDateString(startTime)} </span>
        <span className="text-primary">
          {startTime
            .toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })
            .toLowerCase()}{" "}
        </span>
        <span>{textIdentifier['to'][lang]} </span>
        <span>{getDateString(endTime)} </span>
        <span className="text-primary">
          {endTime
            .toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })
            .toLowerCase()}{" "}
        </span>
      </Fragment>
    );
  };

  if (isFetching) {
    return <Header />;
  }

  return (
    <Fragment>
      <Header />
      <Main className={"competition-school-classes"}>
        <div className="cr-wrapper-card">
          <div className="cr-card-header justify-content-start">
            <div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 35 35"
                fill="none"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M21.78 3.66699H10.2213C6.1933 3.66699 3.66797 6.51899 3.66797 10.555V21.4457C3.66797 25.4817 6.17997 28.3337 10.2213 28.3337H21.7786C25.82 28.3337 28.3346 25.4817 28.3346 21.4457V10.555C28.3346 6.51899 25.82 3.66699 21.78 3.66699Z"
                  stroke="#2E2E2E"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M16 11V16.3333L20 20.3333"
                  stroke="#2E2E2E"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="mx-1">{textIdentifier['from'][lang]} :</span>
            </div>
            <div>{renderTimeStringJSX(startTime, endTime)}</div>
          </div>
          <ol className="breadcrumb">
            <li
              className="breadcrumb-item"
              onClick={() => navigate(`/schools`)}
            >
              {selectedCompetition && <span>{selectedCompetition.name}</span>}
            </li>

            <li className="breadcrumb-item">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="24"
                viewBox="0 0 14 30"
                fill="none"
              >
                <path
                  d="M4.45149 28.6315C4.27728 29.2166 3.95371 29.5946 3.48083 29.7652C3.03283 29.9603 2.57238 29.9846 2.09949 29.8383C1.65149 29.7164 1.30305 29.4604 1.05416 29.0703C0.780383 28.6803 0.743049 28.1804 0.942161 27.5709L9.56616 1.34919C9.76528 0.764046 10.0888 0.373955 10.5368 0.178903C10.9848 -0.0161448 11.4328 -0.0405265 11.8808 0.105759C12.3537 0.227669 12.7146 0.495852 12.9635 0.910332C13.2373 1.30042 13.2746 1.78805 13.0755 2.37319L4.45149 28.6315Z"
                  fill="#2E2E2E"
                />
              </svg>
            </li>

            <li
              className="breadcrumb-item active d-flex align-items-center"
              aria-current="page"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 26 26"
                fill="none"
              >
                <path
                  d="M7.66667 15.6667V19.6667V15.6667ZM13 15.6667V19.6667V15.6667ZM18.3333 15.6667V19.6667V15.6667ZM1 25H25H1ZM1 10.3333H25H1ZM1 6.33333L13 1L25 6.33333H1ZM2.33333 10.3333H23.6667V25H2.33333V10.3333Z"
                  stroke="#2E2E2E"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {selectedSchool && <span>{selectedSchool.name}</span>}
            </li>
          </ol>
          <div className="cr-card">
            <div className="header list-header-container">
              <div className="flex-items">
                <span>{textIdentifier['no'][lang]}.</span>
                <span>{textIdentifier['classes'][lang]}</span>
              </div>
              <div className="dropdown" onClick={toggleShowAdminOptions}>
                <button
                  className="btn btn-outlined dropdown-toggle"
                  type="button"
                  id="dropdownMenuButton1"
                  data-bs-toggle="dropdown"
                  data-bs-offset="-20,15"
                  aria-expanded="false"
                >
                  <span>{textIdentifier["admin-options"][lang]}</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 13 8"
                    fill="none"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M0.351832 0.751112C0.576865 0.526146 0.882035 0.399768 1.20023 0.399768C1.51843 0.399768 1.8236 0.526146 2.04863 0.751112L6.00023 4.70271L9.95183 0.751112C10.0625 0.636499 10.1949 0.545081 10.3413 0.48219C10.4878 0.419299 10.6452 0.386196 10.8046 0.384811C10.9639 0.383426 11.1219 0.413788 11.2694 0.474125C11.4169 0.534462 11.5508 0.623566 11.6635 0.736237C11.7762 0.848908 11.8653 0.982891 11.9256 1.13037C11.986 1.27784 12.0163 1.43586 12.0149 1.59519C12.0135 1.75453 11.9804 1.91199 11.9176 2.0584C11.8547 2.2048 11.7632 2.33721 11.6486 2.44791L6.84863 7.24791C6.6236 7.47288 6.31843 7.59925 6.00023 7.59925C5.68204 7.59925 5.37687 7.47288 5.15183 7.24791L0.351832 2.44791C0.126867 2.22288 0.000488281 1.91771 0.000488281 1.59951C0.000488281 1.28131 0.126867 0.976145 0.351832 0.751112Z"
                      fill="#0059B1"
                    />
                  </svg>
                </button>

                {showAdminOptions && (
                  <ul
                    className="dropdown-menu show"
                    aria-labelledby="dropdownMenuButton1"
                  >
                    <li onClick={() => setShowAddClassModal(true)}>
                      <Link
                        data-bs-toggle="modal"
                        data-bs-target="#addClassModal"
                        className="dropdown-item"
                        to="#addClassModal"
                      >
                        {textIdentifier["add-a-class"][lang]}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 34 34"
                          fill="none"
                        >
                          <path
                            d="M17 11.75V17V11.75ZM17 17V22.25V17ZM17 17H22.25H17ZM17 17H11.75H17ZM32.75 17C32.75 19.0683 32.3426 21.1164 31.5511 23.0273C30.7596 24.9381 29.5995 26.6744 28.1369 28.1369C26.6744 29.5995 24.9381 30.7596 23.0273 31.5511C21.1164 32.3426 19.0683 32.75 17 32.75C14.9317 32.75 12.8836 32.3426 10.9727 31.5511C9.06186 30.7596 7.32559 29.5995 5.86307 28.1369C4.40055 26.6744 3.24041 24.9381 2.4489 23.0273C1.65739 21.1164 1.25 19.0683 1.25 17C1.25 12.8228 2.90937 8.81677 5.86307 5.86307C8.81677 2.90937 12.8228 1.25 17 1.25C21.1772 1.25 25.1832 2.90937 28.1369 5.86307C31.0906 8.81677 32.75 12.8228 32.75 17Z"
                            stroke="#2E2E2E"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </Link>
                    </li>
                  </ul>
                )}
              </div>
            </div>
            <ul className="list-group">
              {classes.map((schoolClass, index) => (
                <React.Fragment key={schoolClass.id}>
                  <li className="list-group-item list-item">
                    <div className="flex-items">
                      <span>{index + 1}</span>
                      <span>{schoolClass.name}</span>
                      <span>({schoolClass.code})</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => selectClass(schoolClass)}
                      className="btn-contained m-0"
                    >
                      {textIdentifier["view"][lang]} {!isViewingStatus ? textIdentifier['students'][lang] : textIdentifier['status'][lang]}
                    </button>
                  </li>
                  <hr className="underline" />
                </React.Fragment>
              ))}
              <li className="list-group-item list-item">
                <div className="flex-items d-block">
                  <span className="mx-2 font-weight-bold">
                    {textIdentifier["total-classes"][lang]} : {classes.length}
                  </span>
                </div>
              </li>
              <hr className="underline" />
            </ul>
          </div>
        </div>
      </Main>
      {showAddClassModal && (
        <Fragment>
          <div
            className="modal fade show"
            id="addClassModal"
            tabIndex="-1"
            aria-labelledby="Add Class Modal"
          >
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-body">
                  <span
                    className="modal-title"
                    onClick={() => setShowAddClassModal(true)}
                  >
                    {textIdentifier["add-a-class"][lang]}
                  </span>
                  <form onSubmit={addClassForm.handleSubmit}>
                    <div className="form-group">
                      <label htmlFor="className" className="form-label">
                        {textIdentifier["enter-class-name"][lang]}
                      </label>
                      <input
                        placeholder= {textIdentifier['eg-class-name'][lang]}
                        className="form-control"
                        id="className"
                        value={addClassForm.values.className}
                        onChange={addClassForm.handleChange}
                      />
                    </div>
                    <div className="form-group">
                      <label
                        htmlFor="classCodeExtension"
                        className="form-label"
                      >
                        {textIdentifier["enter-class-code-extension"][lang]}
                      </label>
                      <div className="classCodeContainer">
                        <span>{textIdentifier["code"][lang]} - {selectedSchool.code}</span>
                        <input
                          placeholder= {textIdentifier["eg-a3"][lang]}
                          className="form-control w-25"
                          id="classCodeExtension"
                          value={addClassForm.values.classCodeExtension}
                          onChange={addClassForm.handleChange}
                        />
                      </div>
                      {addClassForm.errors?.className && (
                        <div id="helperText" className="form-text">
                          {textIdentifier["please-enter-class-name"][lang]}
                        </div>
                      )}
                      {addClassForm.errors?.classCodeExtension && (
                        <div id="helperText" className="form-text">
                          {textIdentifier["please-enter-class-code-extension"][lang]}
                        </div>
                      )}
                      {Boolean(error) && (
                        <div id="helperText" className="form-text">
                          {error}
                        </div>
                      )}
                    </div>
                    <div className="btn-container">
                      <button
                        type="button"
                        className="btn-outlined w-50"
                        onClick={handleCloseAddClassForm}
                      >
                        {textIdentifier["cancel"][lang]}
                      </button>
                      <button
                        type="submit"
                        className="btn-contained w-50"
                        disabled={addClassForm.isSubmitting}
                      >
                        {textIdentifier["confirm"][lang]}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </Fragment>
      )}
    </Fragment>
  );
};

export default ClassesList;
